import React, { ReactNode } from "react";
import { Flex, Grid, Text } from "@chakra-ui/react";
import StatusBadge from "@/shared/components/StatusBadge";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import {
  ORDER_DETAIL_CARD_SX,
  ORDER_DETAIL_KEY,
} from "@/features/pathpick/features/order-history/features/detail/constants";
import { PathPickOrder } from "@/features/pathpick/features/order-history/types/response";
import { isPathPickOrderQuantityConfirmed } from "@/features/pathpick/features/order-history/utils";
import {
  isPathPickOrderCancelDetermined,
  selectPathPickOrderSummaryAmount,
} from "@/features/pathpick/features/order-history/features/detail/utils";

interface Props {
  order: PathPickOrder;
}

const HAIRLINE = "0.5px solid #EFEFED";

// 상단 요약 스트립 — 금액 / 요청 수량 / 확정 수량 / 취소 수량.
// 데스크탑 4열, 모바일 2×2.
function PathPickOrderDetailSummary({ order }: Props) {
  const { t, tCurrencyString } = useI18nHelper();
  const isQuantityConfirmed = isPathPickOrderQuantityConfirmed(order);
  const isCancelDetermined = isPathPickOrderCancelDetermined(order);
  const summaryAmount = selectPathPickOrderSummaryAmount(order);

  // 수량 필드가 비어 오면 0으로 단정하지 않고 "-"로 표기한다.
  const quantityValue = (count: number | null | undefined) =>
    count == null
      ? "-"
      : t("path_pick:order_history.quantity_value", { count });

  return (
    <Grid
      templateColumns={{
        base: "repeat(2, minmax(0, 1fr))",
        md: "repeat(4, 1fr)",
      }}
      sx={ORDER_DETAIL_CARD_SX}
      overflow={"hidden"}
    >
      {/* 금액 — 수량 확정 전에는 "요청 기준 금액 + 확정 전" 배지가 붙는다 */}
      <SummaryCell index={0}>
        <Flex gap={"6px"} alignItems={"center"}>
          <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
            {t(summaryAmount.titleKey)}
          </Text>
          {summaryAmount.badgeKey && (
            <StatusBadge
              status={t(summaryAmount.badgeKey)}
              colorVariant={"yellow"}
              size={"small"}
            />
          )}
        </Flex>
        <Flex flexDirection={"column"} gap={"1px"}>
          {/* 확정 후에는 주문(요청) 금액을 취소선으로 위에 남긴다(시안) */}
          {summaryAmount.struckAmount != null && (
            <Text
              textStyle={"body-sm-regular"}
              color={"#6F6F6C"}
              textDecoration={"line-through"}
            >
              {tCurrencyString(summaryAmount.struckAmount, {
                type: "SYMBOL",
                code: order.currency,
              })}
            </Text>
          )}
          {/* 주문 금액은 확정 전후 모두 시안의 네이비로 강조한다 */}
          <Text textStyle={"body-lg-bold"} color={"#00417D"}>
            {summaryAmount.amount == null
              ? "-"
              : tCurrencyString(summaryAmount.amount, {
                  type: "SYMBOL",
                  code: order.currency,
                })}
          </Text>
        </Flex>
        <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
          {t(summaryAmount.noteKey)}
        </Text>
      </SummaryCell>
      <SummaryStat
        index={1}
        label={t(`${ORDER_DETAIL_KEY}requested_qty`)}
        value={quantityValue(order.totalOrderedQuantity)}
      />
      {/* 확정 전에는 0과 "미결정"을 값만으로 구분할 수 없어 "확인 중"으로 표기한다 */}
      <SummaryStat
        index={2}
        label={t(`${ORDER_DETAIL_KEY}confirmed_qty`)}
        value={
          isQuantityConfirmed
            ? quantityValue(order.totalConfirmedQuantity)
            : t(`${ORDER_DETAIL_KEY}qty_checking`)
        }
      />
      <SummaryStat
        index={3}
        label={t(`${ORDER_DETAIL_KEY}canceled_qty`)}
        value={
          isCancelDetermined ? quantityValue(order.totalCanceledQuantity) : "-"
        }
        isEmphasized={
          isCancelDetermined && (order.totalCanceledQuantity ?? 0) > 0
        }
      />
    </Grid>
  );
}

// 셀 하나의 셸. 헤어라인은 열 수가 달라지면 위치가 달라지므로 인덱스로만 계산한다 —
// 4열에서는 마지막 칸만 세로선이 없고, 2열에서는 왼쪽 칸에만 세로선 + 윗줄에 가로선이 붙는다.
// gap + background로 선을 만드는 방식은 데스크탑 픽셀이 바뀌므로 쓰지 않는다.
function SummaryCell({
  index,
  isCentered,
  children,
}: {
  index: number;
  // 수량 3칸만 가운데 정렬이고, 금액 칸은 기본(stretch) 정렬을 그대로 쓴다.
  isCentered?: boolean;
  children: ReactNode;
}) {
  return (
    <Flex
      flexDirection={"column"}
      gap={"6px"}
      padding={"16px"}
      // 좁은 칸에서 긴 금액이 열을 밀어내지 않게 한다. md는 기존대로 auto.
      minWidth={{ base: 0, md: "auto" }}
      alignItems={isCentered ? "center" : undefined}
      justifyContent={isCentered ? "center" : undefined}
      borderRight={{
        base: index % 2 === 0 ? HAIRLINE : undefined,
        md: index === 3 ? undefined : HAIRLINE,
      }}
      borderBottom={{ base: index < 2 ? HAIRLINE : undefined, md: undefined }}
    >
      {children}
    </Flex>
  );
}

function SummaryStat({
  index,
  label,
  value,
  isEmphasized,
}: {
  index: number;
  label: string;
  value: string;
  isEmphasized?: boolean;
}) {
  return (
    <SummaryCell index={index} isCentered>
      <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
        {label}
      </Text>
      <Text
        textStyle={"body-lg-bold"}
        color={isEmphasized ? "#EE7D7A" : "#1D1D1B"}
      >
        {value}
      </Text>
    </SummaryCell>
  );
}

export default PathPickOrderDetailSummary;
